import React, { forwardRef } from 'react';
import { GameState } from '../../domain/entities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { iconMap, getLeagueColor } from '../../utils/iconUtils';
import { TeamBadge } from '../shared/TeamBadge';
import './Header.css';

interface View {
    type: string;
    title: string;
    [key: string]: any;
}

interface HeaderProps {
    gameState: GameState;
    title: string;
    canGoBack: boolean;
    canGoForward: boolean;
    onBack: () => void;
    onForward: () => void;
    onNavigate: (view: View) => void;
    onToggleMobileNav: () => void;
    unreadNewsCount: number;
    unreadBidsCount?: number;
    headerLeftRef?: React.RefObject<HTMLDivElement>;
    onOpenSearch?: () => void;
    onOpenBidsModal?: () => void;
}

export const Header = forwardRef<HTMLElement, HeaderProps>(({ gameState, title, canGoBack, canGoForward, onBack, onForward, onNavigate, onToggleMobileNav, unreadNewsCount, unreadBidsCount, headerLeftRef, onOpenSearch, onOpenBidsModal }, ref) => {
  const playerLeague = gameState.leagues[gameState.playerLeagueName];
  if (!playerLeague) return null; // Defensive check

  const playerTeam = playerLeague.teams.find(t => t.name === gameState.playerTeamName);
  const balance = playerTeam ? playerTeam.finances.balance : 0;

  // Calculate position based on league standings (sorted by points, goal difference, goals for)
  const standings = [...playerLeague.teams].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    const gdA = a.goalsFor - a.goalsAgainst;
    const gdB = b.goalsFor - b.goalsAgainst;
    if (gdB !== gdA) return gdB - gdA;
    return b.goalsFor - a.goalsFor;
  });
  const playerPosition = standings.findIndex(t => t.name === gameState.playerTeamName) + 1;

  const displayWeek = Math.floor(gameState.currentWeek);

  const getOrdinalSuffix = (num: number) => {
    if (num <= 0) return num;
    const s = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return num + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return (
    <header className="header" ref={ref}>
      <div className="header-left-section" ref={headerLeftRef}>
        <button className="mobile-nav-toggle-button" onClick={onToggleMobileNav} aria-label="Open navigation menu">
          <div className="hamburger-icon">
              <svg viewBox="0 0 100 80" width="20" height="20" fill="currentColor">
                  <rect width="100" height="15"></rect>
                  <rect y="32.5" width="100" height="15"></rect>
                  <rect y="65" width="100" height="15"></rect>
              </svg>
          </div>
        </button>
        <div className="header-team-name-container">
            <button className="header-team-name-button" onClick={() => onNavigate({ type: 'play', title: 'Home' })} title="Go to Home">
                <h1>
                    {playerTeam && <TeamBadge badge={playerTeam.badge} color={playerTeam.colors.primary} size="md" />}
                    {gameState.playerTeamName}
                </h1>
            </button>
        </div>
        {onOpenSearch && (
          <button
            className="header-search-button"
            onClick={onOpenSearch}
            title="Search players and teams"
            aria-label="Open search"
          >
            <FontAwesomeIcon icon={iconMap['search']} />
          </button>
        )}
      </div>
      
      <div className="header-screen-title">
        <button onClick={onBack} disabled={!canGoBack} className="header-nav-btn">{'<'}</button>
        <h2>{title}</h2>
        <button onClick={onForward} disabled={!canGoForward} className="header-nav-btn">{'>'}</button>
      </div>

      <div className="header-stats">
        {unreadNewsCount > 0 && (
            <button
                className="header-stat-item header-stat-news"
                onClick={() => onNavigate({ type: 'inbox', title: 'Inbox' })}
                title={`Go to Inbox (${unreadNewsCount} unread)`}
            >
                <span className="header-stat-icon" style={{ position: 'relative' }}>
                    <FontAwesomeIcon icon={iconMap['inbox']} style={{ color: 'var(--negative-color)' }} />
                    {unreadNewsCount > 0 && (
                        <span style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            backgroundColor: 'var(--accent-color)',
                            color: 'var(--background-color)',
                            borderRadius: '50%',
                            width: '18px',
                            height: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.65em',
                            fontWeight: 'bold',
                            border: '2px solid var(--background-color)',
                            boxShadow: '0 0 8px rgba(var(--accent-color-rgb), 0.5)'
                        }}>
                            {unreadNewsCount}
                        </span>
                    )}
                </span>
                <div className="header-stat-text">
                    <span className="header-stat-label">Inbox</span>
                </div>
            </button>
        )}
        {unreadBidsCount !== undefined && unreadBidsCount > 0 && onOpenBidsModal && (
            <button
                className="header-stat-item header-stat-bids"
                onClick={onOpenBidsModal}
                title="View Incoming Transfer Bids"
                style={{ opacity: unreadBidsCount && unreadBidsCount > 0 ? 1 : 0.6 }}
            >
                <span className="header-stat-icon"><i className="fas fa-gavel" style={{ color: 'var(--accent-color)' }} /></span>
                <div className="header-stat-text">
                    <span className="header-stat-label">Bids</span>
                    <strong>{unreadBidsCount || 0} Pending</strong>
                </div>
            </button>
        )}
        <button className="header-stat-item" onClick={() => onNavigate({ type: 'competitions', title: 'Competitions', activeTab: 'all_fixtures' })} title="Go to All Fixtures">
            <span className="header-stat-icon"><FontAwesomeIcon icon={iconMap['calendar']} style={{ color: 'var(--accent-color)' }} /></span>
            <div className="header-stat-text">
                <span className="header-stat-label">Date</span>
                <strong>S{gameState.season} W{displayWeek}</strong>
            </div>
        </button>
        <button className="header-stat-item" onClick={() => onNavigate({ type: 'competitions', title: 'Competitions', activeTab: 'league' })} title="Go to League Standings">
            <span className="header-stat-icon"><FontAwesomeIcon icon={iconMap['competitions']} style={{ color: getLeagueColor(gameState.playerLeagueName, gameState.config.LEAGUE_NAMES) }} /></span>
            <div className="header-stat-text">
                <span className="header-stat-label">{gameState.playerLeagueName}</span>
                <strong>{getOrdinalSuffix(playerPosition)}</strong>
            </div>
        </button>
        <button className="header-stat-item" onClick={() => onNavigate({ type: 'finances', title: 'Finances' })} title="Go to Finances">
            <span className="header-stat-icon"><FontAwesomeIcon icon={iconMap['finances']} style={{ color: 'var(--warning-color)' }} /></span>
            <div className="header-stat-text">
                <span className="header-stat-label">Balance</span>
                <strong style={{color: balance < 0 ? 'var(--negative-color)' : (balance > 0 ? 'var(--positive-color)' : 'var(--text-color)')}}>
                    £{balance.toLocaleString()}
                </strong>
            </div>
        </button>
      </div>
    </header>
  );
});